import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import { EMPTY, interval, Observable, ReplaySubject } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import { EnvironmentService } from '../services/environment.service';
import { FeatureFlags } from '@enums/feature-flags.enum';
import { Injectable, inject } from '@angular/core';
import { SecurityContextService } from '@services/security_context.service';

@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  private client!: LaunchDarkly.LDClient;
  private environmentService = inject(EnvironmentService);
  private securityContextService = inject(SecurityContextService);
  private activeListeners: Partial<
    Record<FeatureFlags, ReplaySubject<boolean>>
  > = {};

  LAUNCH_DARKLY_CLIENT_ID = this.environmentService.LAUNCH_DARKLY_CLIENT_ID;

  initializeLaunchDarkly(): void {
    const authData = this.securityContextService.authData();

    if (Object.keys(authData).length > 0) {
      const multiContext = {
        kind: 'multi',
        user:{
          kind: 'user',
          name: `${authData.firstname} ${authData.lastname}`,
          key: authData.id,
          anonymous: false,
        } ,
        franchise_group: {
            kind: 'franchise_group',
            key: authData.franchisecode,
            anonymous: false,
          },
      };

      if (this.LAUNCH_DARKLY_CLIENT_ID) {
        this.client = LaunchDarkly.initialize(
          this.LAUNCH_DARKLY_CLIENT_ID,
          multiContext,
        );
      }
    }
  }

  /**
   * To get the LD flag and it's current status
   * @param flagKey key of the key. Can be found in Launch Darkly UI in corresponding flag, should be defined in enum
   * @param defaultValue  - expected to be false in most cases.
   * @returns An observable that emits the state of the flag once the client is initialized.
   * If the client is not initialized, it waits until the client is ready and then fetches the flag.
   * @NOTE If the flag is not available in current environment - the default value will be returned
   */
  getFlag(
    flagKey: FeatureFlags,
    defaultValue: LaunchDarkly.LDFlagValue = false,
  ): Observable<LaunchDarkly.LDFlagValue> {
    if (!this.client) {
      return this.waitForClient().pipe(
        switchMap(() => this.getFlag(flagKey, defaultValue)),
      );
    }

    if (!this.activeListeners[flagKey]) {
      this.activeListeners[flagKey] =
        new ReplaySubject<LaunchDarkly.LDFlagValue>(1); // Ensure it stores the last value
      this.client.waitUntilReady().then(() => {
        try {
          const flagValue = this.client.variation(
            flagKey,
          ) as LaunchDarkly.LDFlagValue;
          this.activeListeners[flagKey]?.next(flagValue);

          // Listen for flag changes
          this.client.on(
            `change:${flagKey}`,
            (newValue: LaunchDarkly.LDFlagValue) => {
              this.activeListeners[flagKey]?.next(newValue);
            },
          );
        } catch (error) {
          console.error('Error fetching flag value:', error);
          this.activeListeners[flagKey]?.next(defaultValue);
        }
      });
    }
    return this.activeListeners[flagKey].asObservable();
  }

  waitForClient(): Observable<number> {
    if (this.client) {
      return EMPTY;
    }

    return interval(100).pipe(
      filter(() => Boolean(this.client)),
      take(1),
    );
  }
}
